import React, { useMemo } from "react";
import {
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  ReferenceLine,
  Label,
  ResponsiveContainer,
} from "recharts";
import { convertSpeed } from "../lib/pace";

// A curated palette — lactate curves are the hero, so use saturated brand-friendly tones.
// Each entry is [lactateColor, hrColor].
const SERIES_PALETTE = [
  ["#7A1F2B", "#1E3A8A"], // burgundy / navy
  ["#B8860B", "#2E7D6B"], // dark-gold / pine
  ["#6B4E2E", "#455A64"], // bronze / slate
  ["#4A235A", "#27496D"], // eggplant / deep blue
];

function toTestPoints(test, displayUnit) {
  return (test.rows || [])
    .filter((r) => r.speed != null && r.speed !== "" && Number.isFinite(Number(r.speed)))
    .map((r) => ({
      speed: Number(convertSpeed(r.speed, test.speed_unit || "mph", displayUnit)),
      lactate:
        r.blood_lactate != null && r.blood_lactate !== "" ? Number(r.blood_lactate) : null,
      hr: r.hr != null && r.hr !== "" ? Number(r.hr) : null,
    }))
    .sort((a, b) => a.speed - b.speed);
}

function formatShortDate(iso) {
  if (!iso) return "—";
  try {
    const m = typeof iso === "string" && /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "2-digit",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Overlay lactate (and optional HR) curves from multiple tests, normalised to a single display unit.
 * tests: ordered oldest → newest (recent gets darker/thicker).
 */
export default function CompareChart({ tests, displayUnit = "mph", showHR = false, height = 460 }) {
  const series = useMemo(() => {
    // convert each test's rows to the display unit
    return tests.map((t, i) => ({
      id: t.id,
      label: formatShortDate(t.test_date),
      points: toTestPoints(t, displayUnit),
      lactateColor: SERIES_PALETTE[i % SERIES_PALETTE.length][0],
      hrColor: SERIES_PALETTE[i % SERIES_PALETTE.length][1],
      lt1Speed:
        t.lt1?.speed != null && t.lt1?.speed !== ""
          ? Number(convertSpeed(t.lt1.speed, t.speed_unit || "mph", displayUnit))
          : null,
      lt2Speed:
        t.lt2?.speed != null && t.lt2?.speed !== ""
          ? Number(convertSpeed(t.lt2.speed, t.speed_unit || "mph", displayUnit))
          : null,
      isNewest: i === tests.length - 1,
    }));
  }, [tests, displayUnit]);

  // unified x-domain
  const speedDomain = useMemo(() => {
    const all = series.flatMap((s) => s.points.map((p) => p.speed)).filter(Number.isFinite);
    if (all.length === 0) return [0, 10];
    const min = Math.min(...all);
    const max = Math.max(...all);
    const pad = Math.max(0.3, (max - min) * 0.08);
    return [Math.max(0, +(min - pad).toFixed(2)), +(max + pad).toFixed(2)];
  }, [series]);

  // union of all points to let recharts draw multiple lines via separate dataKeys
  const merged = useMemo(() => {
    const keyed = new Map();
    series.forEach((s) => {
      s.points.forEach((p) => {
        const key = p.speed.toFixed(3);
        if (!keyed.has(key)) keyed.set(key, { speed: p.speed });
        const row = keyed.get(key);
        row[`lac_${s.id}`] = p.lactate;
        row[`hr_${s.id}`] = p.hr;
      });
    });
    return Array.from(keyed.values()).sort((a, b) => a.speed - b.speed);
  }, [series]);

  return (
    <div className="w-full" data-testid="compare-chart">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={merged} margin={{ top: 32, right: 56, left: 8, bottom: 28 }}>
          <CartesianGrid stroke="#E7E5E4" strokeDasharray="2 4" vertical={false} />
          <XAxis
            type="number"
            dataKey="speed"
            domain={speedDomain}
            tickCount={8}
            stroke="#57534E"
            tick={{ fill: "#1C1917", fontSize: 12, fontFamily: "Montserrat" }}
            tickFormatter={(v) => v.toFixed(1)}
          >
            <Label
              value={`Speed (${displayUnit.toUpperCase()})`}
              position="insideBottom"
              offset={-14}
              style={{
                fill: "#1C1917",
                fontFamily: "Montserrat",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
              }}
            />
          </XAxis>
          <YAxis
            yAxisId="lactate"
            orientation="left"
            stroke="#7A1F2B"
            tick={{ fill: "#7A1F2B", fontSize: 12, fontFamily: "Montserrat" }}
            domain={[0, (dataMax) => Math.max(10, Math.ceil((dataMax || 0) + 1))]}
          >
            <Label
              angle={-90}
              value="Blood Lactate (mmol/L)"
              position="insideLeft"
              offset={10}
              style={{
                fill: "#7A1F2B",
                fontFamily: "Montserrat",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                textAnchor: "middle",
              }}
            />
          </YAxis>
          {showHR && (
            <YAxis
              yAxisId="hr"
              orientation="right"
              stroke="#1E3A8A"
              tick={{ fill: "#1E3A8A", fontSize: 12, fontFamily: "Montserrat" }}
              domain={[80, (dataMax) => Math.max(200, Math.ceil((dataMax || 0) + 10))]}
            >
              <Label
                angle={90}
                value="Heart Rate (bpm)"
                position="insideRight"
                offset={10}
                style={{
                  fill: "#1E3A8A",
                  fontFamily: "Montserrat",
                  fontSize: 12,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  textAnchor: "middle",
                }}
              />
            </YAxis>
          )}
          <Tooltip
            contentStyle={{
              background: "#fff",
              border: "1px solid #1C1917",
              borderRadius: 0,
              fontFamily: "Montserrat",
            }}
            labelFormatter={(v) => `Speed: ${Number(v).toFixed(2)} ${displayUnit}`}
          />
          <Legend
            verticalAlign="top"
            height={28}
            iconType="plainline"
            wrapperStyle={{
              fontFamily: "Montserrat",
              fontSize: 12,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          />
          {series.map((s) => (
            <Line
              key={`lac-${s.id}`}
              yAxisId="lactate"
              name={`${s.label} · Lactate`}
              type="monotone"
              dataKey={`lac_${s.id}`}
              stroke={s.lactateColor}
              strokeWidth={s.isNewest ? 3 : 2}
              strokeDasharray={s.isNewest ? "0" : "5 4"}
              dot={{ r: 3.5, fill: s.lactateColor, strokeWidth: 0 }}
              activeDot={{ r: 6 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
          {showHR &&
            series.map((s) => (
              <Line
                key={`hr-${s.id}`}
                yAxisId="hr"
                name={`${s.label} · HR`}
                type="monotone"
                dataKey={`hr_${s.id}`}
                stroke={s.hrColor}
                strokeWidth={s.isNewest ? 2.5 : 1.75}
                strokeDasharray={s.isNewest ? "0" : "5 4"}
                dot={{ r: 3, fill: s.hrColor, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          {series.map((s) =>
            s.lt2Speed != null ? (
              <ReferenceLine
                key={`lt2-${s.id}`}
                yAxisId="lactate"
                x={s.lt2Speed}
                stroke={s.lactateColor}
                strokeDasharray="3 3"
                strokeOpacity={0.6}
                strokeWidth={s.isNewest ? 1.5 : 1}
              >
                <Label
                  value={`LT2 · ${s.label}`}
                  position="top"
                  style={{ fill: s.lactateColor, fontFamily: "Montserrat", fontSize: 10, fontWeight: 600 }}
                />
              </ReferenceLine>
            ) : null
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
